import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getCurrentProfile, getSystemInit } from '../api'
import { ErrorPageInit } from '../components/ErrorPage'
import { Loader } from '../components/Loader'
import { DEFAULT_TIME_ZONE } from '../constants/date'
import {
  InitActionsContext,
  InitDataContext,
  UserDataContext,
} from '../context'
import {
  useApplyCookieToken,
  useJWT,
  usePageBrowserTabTitle,
  useSeoInitialValues,
} from '../hooks'
import { getRefetchedInitData } from '../utils/init'

import InitDateTimeProvider from './InitDateTimeProvider'

function InitProvider({ children }) {
  const [initData, setInitData] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isInitDataError, setInitDataError] = useState(false)
  const [isUserProfileError, setUserProfileError] = useState(false)
  const [language, setLanguage] = useState(null)

  const { i18n } = useTranslation()

  const { checkJWT, removeJWT } = useJWT()

  useApplyCookieToken()

  usePageBrowserTabTitle({ isLoading: true })

  useSeoInitialValues(initData)

  async function fetchInitData() {
    return getSystemInit()
      .then(response => {
        setInitData(prevInitData =>
          getRefetchedInitData(prevInitData, response.data)
        )
        setInitDataError(false)
      })
      .catch(() => {
        setInitDataError(true)
      })
  }

  async function fetchUserData() {
    return getCurrentProfile()
      .then(response => {
        setUserData(response.data)
        setUserProfileError(false)
      })
      .catch(() => {
        setUserProfileError(true)
      })
  }

  const actions = {
    refetchInitData: fetchInitData,
    refetchUserData: fetchUserData,
    resetApp: () => {
      setInitData(null)
      setUserData(null)
    },
    setUkTheme: theme => {
      setInitData(prev => ({
        ...prev,
        uk: { ...prev.uk, config: { ...prev.uk.config, theme } },
      }))
    },
  }

  useEffect(() => {
    if (!initData) {
      fetchInitData()
    } else if (initData.authorized) {
      fetchUserData()
    }
  }, [initData])

  useEffect(() => {
    if (initData) {
      if (initData.authorized) {
        checkJWT()
      } else {
        removeJWT()
      }
    }
  }, [initData])

  useEffect(() => {
    const language = initData
      ? initData.authorized
        ? userData
          ? userData.language.code
          : null
        : initData.uk.language.code
      : null

    if (language) {
      i18n.changeLanguage(language).then(() => {
        setLanguage(language)
      })
    }
  }, [i18n, initData, userData])

  const isLoading = !initData || (initData.authorized && !userData) || !language

  // The alias keeps consumers off the raw `ui_sections` field name.
  const initDataValue = initData
    ? { ...initData, availableSections: initData.ui_sections }
    : null

  return (
    <InitDataContext value={initDataValue}>
      <UserDataContext value={userData}>
        <InitActionsContext value={actions}>
          {isInitDataError || isUserProfileError ? (
            <ErrorPageInit />
          ) : isLoading ? (
            <Loader />
          ) : (
            <InitDateTimeProvider
              language={language}
              timeZone={initData.uk.city.timezone ?? DEFAULT_TIME_ZONE}
            >
              {children}
            </InitDateTimeProvider>
          )}
        </InitActionsContext>
      </UserDataContext>
    </InitDataContext>
  )
}

export default InitProvider
